import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { X, Cookie, ChevronRight } from 'lucide-react';
import {
  acceptAllCookies,
  rejectNonEssentialCookies,
  saveCookiePreferences,
  selectShouldShowBanner,
} from '../../store/slices/cookieConsentSlice';

/**
 * CookieBanner — first-visit cookie consent.
 *
 * Bottom-of-screen sliding band on desktop, full-width modal-ish band on
 * mobile. Three actions: Accept all, Reject non-essential, Customize.
 *
 * Design system anchors:
 *   - Surface: cream (--color-cream #F9F6EE) with charcoal text
 *   - Sharp corners (no radius on the band itself)
 *   - Primary: gold + charcoal text (preferred CTA pairing)
 *   - Secondary: outlined charcoal
 *   - Tertiary "Customize": underline-on-hover gold link
 *   - prefers-reduced-motion safe (animations are CSS, no JS-driven motion)
 *
 * Persistence: handled by the cookieConsent slice + redux-persist.
 *
 * Categories:
 *   - essential : always on (auth cookies, cart persistence)
 *   - analytics : opt-in (e.g., Google Analytics — not yet wired)
 *   - marketing : opt-in (e.g., ad pixels — not yet wired)
 */
export default function CookieBanner() {
  const dispatch = useDispatch();
  const visible = useSelector(selectShouldShowBanner);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  if (!visible) return null;

  const onAcceptAll = () => dispatch(acceptAllCookies());
  const onRejectAll = () => dispatch(rejectNonEssentialCookies());
  const onSave = () => dispatch(saveCookiePreferences(prefs));

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      aria-live="polite"
      className="fixed bottom-0 left-0 right-0 z-[60] bg-cream-100 border-t border-gray-200 shadow-2xl animate-fade-in"
      style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 60 }}
    >
      <div className="page-container py-5 md:py-6">
        {!expanded ? (
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <span className="flex-shrink-0 w-9 h-9 rounded-full bg-gold/15 flex items-center justify-center text-gold mt-0.5">
                <Cookie size={17} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg md:text-xl text-charcoal leading-tight">
                  We use cookies to keep this site working.
                </p>
                <p className="font-sans text-sm text-warm-stone mt-1.5 leading-relaxed">
                  Essential cookies sign you in and remember your cart. Optional
                  cookies help us understand how the site is used and improve it.
                  See our{' '}
                  <Link to="/cookies" className="text-charcoal underline-offset-2 hover:text-gold hover:underline transition-colors">
                    cookie policy
                  </Link>
                  {' '}for details.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="font-sans text-xs tracking-widest uppercase text-charcoal hover:text-gold transition-colors px-3 py-2 flex items-center justify-center gap-1"
              >
                Customize <ChevronRight size={13} />
              </button>
              <button
                type="button"
                onClick={onRejectAll}
                className="border border-charcoal text-charcoal font-sans text-xs tracking-widest uppercase px-5 py-2.5 hover:bg-charcoal hover:text-white transition-colors"
              >
                Essential only
              </button>
              <button
                type="button"
                onClick={onAcceptAll}
                className="bg-gold text-charcoal font-sans text-xs tracking-widest uppercase font-medium px-5 py-2.5 hover:bg-charcoal hover:text-white transition-colors"
              >
                Accept all
              </button>
            </div>
          </div>
        ) : (
          /* ── Expanded preferences view ── */
          <div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-serif text-lg md:text-xl text-charcoal leading-tight">
                  Cookie preferences
                </p>
                <p className="font-sans text-sm text-warm-stone mt-1.5 max-w-xl">
                  Choose which cookies you allow. Essential cookies are required for
                  the site to work and can't be turned off.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setExpanded(false)}
                aria-label="Close"
                className="text-warm-stone hover:text-charcoal transition-colors p-1"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-5 space-y-3 max-w-2xl">
              <CategoryRow
                title="Essential"
                description="Authentication, cart persistence, and CSRF protection. Always on."
                value={true}
                disabled
              />
              <CategoryRow
                title="Analytics"
                description="Helps us understand which pages and products are most useful."
                value={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              />
              <CategoryRow
                title="Marketing"
                description="Used to measure the effectiveness of our marketing campaigns."
                value={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                type="button"
                onClick={onRejectAll}
                className="border border-charcoal text-charcoal font-sans text-xs tracking-widest uppercase px-5 py-2.5 hover:bg-charcoal hover:text-white transition-colors"
              >
                Essential only
              </button>
              <button
                type="button"
                onClick={onSave}
                className="bg-gold text-charcoal font-sans text-xs tracking-widest uppercase font-medium px-5 py-2.5 hover:bg-charcoal hover:text-white transition-colors"
              >
                Save preferences
              </button>
              <button
                type="button"
                onClick={onAcceptAll}
                className="font-sans text-xs tracking-widest uppercase text-charcoal hover:text-gold transition-colors px-3 py-2.5"
              >
                Accept all
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryRow({ title, description, value, onChange, disabled }) {
  return (
    <div className="flex items-start justify-between gap-5 px-4 py-3 bg-white border border-gray-100">
      <div className="flex-1 min-w-0">
        <p className="font-sans text-sm font-medium text-charcoal tracking-wide uppercase">
          {title}
        </p>
        <p className="font-sans text-xs text-warm-stone mt-1 leading-relaxed">
          {description}
        </p>
      </div>
      <Toggle value={value} onChange={onChange} disabled={disabled} aria-label={`${title} cookies`} />
    </div>
  );
}

function Toggle({ value, onChange, disabled, ...rest }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!value)}
      className={`relative w-10 h-5 flex-shrink-0 transition-colors duration-200 mt-0.5 ${
        value ? 'bg-gold' : 'bg-gray-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      style={{ borderRadius: '999px' }}
      {...rest}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white shadow transition-transform duration-200 ${
          value ? 'translate-x-5' : 'translate-x-0'
        }`}
        style={{ borderRadius: '999px' }}
      />
    </button>
  );
}
